import { Link, useLocation } from "react-router";
import { twJoin } from "tailwind-merge";

import type { ConnectedProjectDto } from "@/api/types";
import { ProjectLogo } from "@/components/ProjectLogo/ProjectLogo";
import { Badge } from "@/design-system/Badge";
import { getProjectSwitchHref } from "@/helpers/getProjectSwitchHref";
import { useTranslation } from "@/translations";

interface Props {
  project: ConnectedProjectDto;
  onClick?: () => void;
}

export function ProjectItem({ project, onClick }: Props): React.ReactNode {
  const { t } = useTranslation();
  const location = useLocation();

  return (
    <Link
      to={getProjectSwitchHref(location.pathname, project.slug)}
      className={twJoin(
        "flex w-full button-press items-center gap-space-md rounded-md p-space-sm outline-hidden hover:bg-action-subtle-primary-hover focus-visible:bg-action-subtle-primary-hover focus-visible:ring-border-default pressed:bg-action-soft-primary",
        project.activeState === "closed" && "opacity-80",
      )}
      onClick={onClick}
    >
      <span className="size-thumbnail-compact shrink-0">
        <ProjectLogo src={project.logoUrl} />
      </span>
      <span className="flex flex-1 flex-col items-start">
        <span className="line-clamp-1 text-left text-body-emphasis break-all">{project.name}</span>
        {project.city && <span className="text-left text-overline text-text-secondary">{project.city}</span>}
      </span>
      {project.activeState === "test" && <Badge variant="special">{t("component.project-item.badge.test")}</Badge>}
      {project.activeState === "demo" && <Badge variant="special">{t("component.project-item.badge.demo")}</Badge>}
      {project.activeState === "closed" && <Badge variant="danger">{t("component.project-item.badge.closed")}</Badge>}
    </Link>
  );
}
